import { useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  Fab,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { fullDateNoSecond } from '@/utilities/dateUtils';
import type { SubscriptionActionResult } from './SubscriptionCard';
import { isValidVatInput, normalizeVatInput } from './subscriptionUtils';

export type SubscriptionCustomerOption = {
  id: number;
  name: string;
};

export type AddSubscriptionResult =
  | { status: 'success' }
  | { status: 'reactivation-required'; vat: string; revokedAtMicros?: number | null }
  | { status: 'error'; message: string };

export function AddSubscriptionForm({
  customer,
  isSubmitting,
  error,
  onClose,
  onSubmit,
  onReactivate,
}: {
  customer: SubscriptionCustomerOption;
  isSubmitting: boolean;
  error: string | undefined;
  onClose: () => void;
  onSubmit: (vat: string) => Promise<AddSubscriptionResult>;
  onReactivate: (vat: string) => Promise<SubscriptionActionResult>;
}) {
  const { t } = useTranslation();
  const [vat, setVat] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [inlineError, setInlineError] = useState<string>();
  const [pendingReactivation, setPendingReactivation] = useState<{
    vat: string;
    revokedAtMicros?: number | null;
  }>();
  const vatIsValid = isValidVatInput(vat);
  const showVatError = vat.trim() !== '' && !vatIsValid;
  const canSubmit = vatIsValid && consentAccepted && !isSubmitting && !isWorking;

  async function handleSubmit() {
    if (!canSubmit) return;
    setInlineError(undefined);
    setIsWorking(true);
    try {
      if (pendingReactivation) {
        const result = await onReactivate(pendingReactivation.vat);
        if (result.status === 'success') {
          onClose();
        } else {
          setInlineError(result.message);
        }
        return;
      }

      const result = await onSubmit(normalizeVatInput(vat));
      if (result.status === 'success') {
        onClose();
      } else if (result.status === 'reactivation-required') {
        setPendingReactivation({ vat: result.vat, revokedAtMicros: result.revokedAtMicros });
      } else {
        setInlineError(result.message);
      }
    } finally {
      setIsWorking(false);
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, p: 1 }}>
        <IconButton
          autoFocus
          onClick={onClose}
          aria-label={t('etransport.subscriptions.closeadd')}
          data-qa-element-id="etr_add_subscription_close"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">{t('etransport.subscriptions.add')}</Typography>
      </Stack>
      <Divider />

      <Stack sx={{ gap: 2, p: 2, pb: 12, overflowY: 'auto' }}>
        <TextField
          value={`${customer.name} (${customer.id})`}
          label={t('etransport.subscriptions.customer')}
          size="small"
          fullWidth
          slotProps={{
            htmlInput: {
              readOnly: true,
              'data-qa-element-id': 'etr_add_subscription_customer',
            },
          }}
        />

        <TextField
          value={vat}
          onChange={(event) => {
            setVat(event.target.value);
            setInlineError(undefined);
            setPendingReactivation(undefined);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') void handleSubmit();
          }}
          label={t('etransport.subscriptions.vat')}
          size="small"
          fullWidth
          error={showVatError}
          helperText={showVatError ? t('etransport.subscriptions.vatinvalid') : undefined}
          disabled={isSubmitting || isWorking}
          slotProps={{ htmlInput: { 'data-qa-element-id': 'etr_add_subscription_vat' } }}
        />

        <FormControlLabel
          sx={{ alignItems: 'flex-start', m: 0 }}
          control={
            <Checkbox
              checked={consentAccepted}
              onChange={(event) => setConsentAccepted(event.target.checked)}
              disabled={isSubmitting || isWorking}
              data-qa-element-id="etr_add_subscription_consent"
            />
          }
          label={
            <Typography variant="body2" sx={{ pt: 1 }}>
              <Trans
                i18nKey="etransport.subscriptions.consent"
                values={{ customer: customer.name }}
                components={{ strong: <Box component="strong" /> }}
              />
            </Typography>
          }
        />

        {pendingReactivation && (
          <Alert severity="warning">
            {t('etransport.subscriptions.reactivatedialog.addmessage', { vat: pendingReactivation.vat })}
            {pendingReactivation.revokedAtMicros != null && (
              <Typography variant="body2">
                {t('etransport.subscriptions.revokedat', {
                  date: fullDateNoSecond(pendingReactivation.revokedAtMicros, t),
                  interpolation: { escapeValue: false },
                })}
              </Typography>
            )}
          </Alert>
        )}

        {(inlineError ?? error) && <Alert severity="error">{inlineError ?? error}</Alert>}
      </Stack>

      <Fab
        variant="extended"
        color="secondary"
        onClick={() => void handleSubmit()}
        disabled={!canSubmit}
        aria-busy={isWorking || isSubmitting}
        aria-label={t(`etransport.subscriptions.${pendingReactivation ? 'reactivate' : 'subscribe'}`)}
        data-qa-element-id="etr_add_subscription_submit"
        sx={{
          position: 'absolute',
          right: isPhoneOrHighScreen() ? 10 : 18,
          bottom: isPhoneOrHighScreen() ? 5 : 20,
          backgroundColor: 'var(--mui-palette-secondary-main)',
          color: 'white',
          border: '2px solid white',
          '&:hover': {
            backgroundColor: 'var(--mui-palette-secondary-light)',
          },
          '&.Mui-disabled': {
            backgroundColor: 'grey',
            color: 'white',
            opacity: 1,
          },
        }}
      >
        {isWorking || isSubmitting ? (
          <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
        ) : (
          <CheckIcon sx={{ mr: 1 }} />
        )}
        {t(`etransport.subscriptions.${pendingReactivation ? 'reactivate' : 'subscribe'}`)}
      </Fab>
    </Box>
  );
}
